// Chargement paresseux résistant aux déploiements (repris de tc_chat_ui).
//
// Un onglet ouvert AVANT un déploiement garde l'ancien main.js en mémoire. Au
// premier composant paresseux rendu après le déploiement, le navigateur
// demande un chunk de l'ancienne image, qui n'existe plus → ChunkLoadError →
// React démonte tout → page blanche.
//
// Sur un échec de chargement de chunk :
//   1. une nouvelle tentative sur place (coupure réseau passagère) ;
//   2. sinon, rechargement de la page UNE fois (garde en sessionStorage,
//      fenêtre de 60 s) : il récupère le nouveau shell et les nouveaux chunks ;
//   3. si la garde refuse, l'erreur remonte à l'ErrorBoundary (écran de repli
//      avec bouton « Recharger »).
import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

const RELOAD_STAMP_KEY = 'tilmicode_chunk_reload_at';
const RELOAD_MIN_INTERVAL_MS = 60_000;
const CHUNK_RETRY_DELAY_MS = 500;

/** Erreur de chargement d'un chunk webpack / module dynamique (tous navigateurs). */
export function isChunkLoadError(error: unknown): boolean {
    if (!error) return false;
    const e = error as { name?: string; message?: string };
    if (e.name === 'ChunkLoadError') return true;
    const msg = String(e.message ?? error);
    return /Loading (CSS )?chunk [\w-]+ failed|dynamically imported module|Importing a module script failed|error loading dynamically imported/i.test(msg);
}

function safeSessionStorage(): Storage | null {
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null;
    } catch {
        return null;
    }
}

/** Vrai si un rechargement automatique est permis maintenant (anti-boucle). */
export function shouldReloadNow(now: number = Date.now(), storage: Storage | null = safeSessionStorage()): boolean {
    if (!storage) return true;
    try {
        const last = Number(storage.getItem(RELOAD_STAMP_KEY) ?? 0);
        return !last || now - last > RELOAD_MIN_INTERVAL_MS;
    } catch {
        return true;
    }
}

export function markReloaded(now: number = Date.now(), storage: Storage | null = safeSessionStorage()): void {
    try {
        storage?.setItem(RELOAD_STAMP_KEY, String(now));
    } catch {
        // stockage indisponible : on recharge quand même, sans garde
    }
}

/**
 * Rechargement de la page, derrière une indirection : `window.location` n'est
 * pas redéfinissable dans tous les environnements jsdom (celui de la CI
 * notamment), les tests espionnent donc cet objet plutôt que `location`.
 */
export const pageNavigation = {
    reload: (): void => window.location.reload(),
};

/** Recharge la page une seule fois par fenêtre de 60 s ; false si la garde refuse. */
export function reloadOnce(): boolean {
    if (!shouldReloadNow()) return false;
    markReloaded();
    pageNavigation.reload();
    return true;
}

/**
 * Enveloppe un import dynamique : une nouvelle tentative sur échec de chunk,
 * puis rechargement unique de la page. Les autres erreurs remontent telles
 * quelles.
 */
export function withChunkRetry<M>(
    factory: () => Promise<M>,
    retryDelayMs: number = CHUNK_RETRY_DELAY_MS,
): () => Promise<M> {
    return async () => {
        try {
            return await factory();
        } catch (error) {
            if (!isChunkLoadError(error)) throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
        try {
            return await factory();
        } catch (error) {
            if (isChunkLoadError(error) && reloadOnce()) {
                // la page se recharge : ne jamais résoudre
                return new Promise<M>(() => undefined);
            }
            throw error;
        }
    };
}

/** React.lazy résistant aux déploiements (voir withChunkRetry). */
export function lazyWithRetry<T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
    return lazy(withChunkRetry(factory));
}
